import { BoxOfferInvitationEntity } from "@modules/boxOffer/entities/boxOfferInvitationEntity"
import { BoxOfferInvitationStatus, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer, useBoxOfferInvitation } from "@modules/boxOffer/hooks/useBoxOffer"
import { getBoxOfferIcon, getOfferCommitment } from "@modules/boxOffer/utils/boxOfferUtils"
import { getFormattedCouponReduction, getPriceAfterDicount } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { motion } from "framer-motion"
import { ChevronDown, ChevronUp, Dot, Tag, Trash2 } from "lucide-react"
import { Skeleton } from "primereact/skeleton"
import { useState } from "react"
import { useTheme } from "styled-components"

type Props = {
  item: BoxOfferInvitationEntity
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferInvitationItem({ item, className, ...props }: Props) {
  const defaultClassName = "card flex flex-column gap-4 mb-0 p-0 px-4 py-3 w-full"
  const theme = useTheme()
  const language = useLanguage()
  const confirmPopup = useConfirmPopup()

  const { data: offer, isLoading } = useBoxOffer(item.boxOfferId)
  const { cancelBoxOfferInvitation, isCancelBoxOfferInvitation } = useBoxOfferInvitation(item.id, item.invitee.id)
  const [isCollapsed, setIsCollapsed] = useState(true)

  const bodyCancelInvitationPopup = (
    <div className="flex flex-column align-items-center align-content-center justify-content-center gap-3">
      <HUText fontStyle="TL" color={theme.mainWhite} className="flex">
        {word("box.invitation.popup.cancel.title")}
      </HUText>
      <HUText fontStyle="BM" color={theme.neutralColor700} className="flex w-22rem text-center">
        {word("box.invitation.popup.cancel.message")}
      </HUText>
    </div>
  )

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev)
  }

  const handleCancel = () => {
    if (isCancelBoxOfferInvitation) return
    confirmPopup.show({
      width: "418px",
      title: "",
      message: bodyCancelInvitationPopup,
      accept: async () => {
        await cancelBoxOfferInvitation()
      },
      footerProps: {
        align: true,
        cancelText: word("global.cancel"),
        confirmText: word("global.continue"),
        confirmClassName: "flex-1",
        cancelClassName: "flex-1",
        footerClassName: "flex flex-row justify-content-around w-full",
      },
    })
  }

  if (isLoading || !offer) return <Skeleton height="4.5rem" borderRadius="20px" />

  return (
    <div className={className ?? defaultClassName} key={item.id} {...props}>
      {/* HEADER */}
      <div className="flex flex-column gap-2 w-full">
        <div className="flex flex-row w-full justify-content-between align-items-center">
          <div className="flex flex-row align-items-center">
            <HUText
              fontStyle="BM"
              color={theme.mainWhite}
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              className="max-w-10rem w-10rem"
            >
              {offer.name}
            </HUText>
            <Dot strokeWidth={10} color={item.getColorStatus(theme)} />
            <HUText
              fontStyle="LS"
              color={theme.neutralColor}
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            >
              {item.getWordingStatus()}
            </HUText>
          </div>
          <div className="flex gap-2">
            {isCollapsed ? (
              <ChevronDown size={25} onClick={toggleCollapse} className="cursor-pointer" />
            ) : (
              <ChevronUp size={25} onClick={toggleCollapse} className="cursor-pointer" />
            )}
          </div>
        </div>
      </div>
      {/* BODY EXPANDABLE */}
      <motion.div
        animate={{ height: isCollapsed ? 0 : "auto" }}
        className={`flex-column gap-3 w-full overflow-hidden ${isCollapsed ? "hidden" : "flex"}`}
      >
        {/* TYPE */}
        <div className="flex flex-column gap-2 w-full">
          <div className="flex w-full gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("box.member.table.offer.item.type")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-200" />
          </div>
          <div className="flex w-full gap-2 align-items-center">
            {getBoxOfferIcon(offer.type)}
            <HUText fontStyle="LL" color={theme.mainWhite}>
              {word(`box.offer.form.types.${offer.type}`)}
            </HUText>
          </div>
        </div>
        {/* STATUS */}
        <div className="flex flex-column gap-2 w-full">
          <div className="flex w-full gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("box.member.table.offer.item.status.label")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-200" />
          </div>
          <div className="flex flex-column w-full gap-1">
            <HUText fontStyle="LL" color={theme.mainWhite}>
              {item.getWordingStatus()}
            </HUText>

            <div className="flex align-items-center gap-1 pl-3">
              <HUText fontStyle="LM" color={theme.mainWhite}>
                {`${word("box.member.table.offer.item.start")} ·`}
              </HUText>
              <HUText fontStyle="BS" color={theme.neutralColor600}>
                {dayjs(item.createdAt).format("L")}
              </HUText>
            </div>

            {item.updatedAtDate &&
              (item.status === BoxOfferInvitationStatus.CANCELLED ||
                item.status === BoxOfferInvitationStatus.REFUSED) && (
                <div className="flex align-items-center gap-1 pl-3">
                  <HUText fontStyle="LM" color={theme.mainWhite}>
                    {item.status === BoxOfferInvitationStatus.CANCELLED
                      ? `${word("box.member.table.offer.item.cancelled")} ·`
                      : `${word("box.member.table.offer.item.refused")} ·`}
                  </HUText>
                  <HUText fontStyle="BS" color={theme.neutralColor600}>
                    {dayjs(item.updatedAtDate).format("L")}
                  </HUText>
                </div>
              )}

            <div className="flex align-items-center gap-1 pl-3">
              <HUText fontStyle="LM" color={theme.mainWhite}>
                {offer.type === BoxOfferType.Subscription || offer.type === BoxOfferType.Millenium
                  ? word("box.member.table.offer.item.commitmentPeriod")
                  : word("box.member.table.offer.item.validityPeriod")}
              </HUText>
              <HUText fontStyle="LM" color={theme.mainWhite}>
                ·
              </HUText>
              <HUText fontStyle="BS" color={theme.neutralColor600}>
                {capitalize(getOfferCommitment(offer))}
              </HUText>
            </div>
          </div>
        </div>
        {/* PRICE */}
        <div className="flex flex-column gap-2 w-full">
          <div className="flex w-full gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor600} className="font-semibold">
              {word("box.member.table.offer.item.price")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-200" />
          </div>
          <div className="flex w-full gap-1 align-items-center">
            {offer.price ? (
              <>
                <HUText fontStyle="LL" color={theme.mainWhite}>
                  {formatCurrency(item.getPriceAfterDicount(offer.price.value), offer.price.currency, language)}
                </HUText>
                {offer.price.value !== item.getPriceAfterDicount(offer.price.value) ? (
                  <HUText fontStyle="LM" color={theme.neutralColor400} className="line-through">
                    {formatCurrency(offer.price.value, offer.price.currency, language)}
                  </HUText>
                ) : null}
              </>
            ) : (
              <HUText fontStyle="LL" color={theme.mainWhite}>
                {word("box.member.table.offer.item.freePrice")}
              </HUText>
            )}
          </div>
          <div className="flex flex-column w-full gap-3">
            {/* DISCOUNT */}
            {item.coupon && offer.price ? (
              <div className="flex flex-column w-full gap-2">
                <div className="flex gap-1 pl-3 align-items-center">
                  <Tag size={15} color={theme.neutralColor} />
                  <HUText fontStyle="LS" color={theme.neutralColor} className="font-semibold">
                    {word("box.member.table.offer.item.discount.label")}
                  </HUText>
                </div>
                <div className="flex flex-column gap-1 pl-3">
                  <HUText fontStyle="BS" color={theme.mainWhite}>
                    {getFormattedCouponReduction(item.coupon.type, item.coupon.value, language, offer.price.currency)} -{" "}
                    {formatCurrency(
                      getPriceAfterDicount(item.coupon, offer.price.value),
                      offer.price.currency,
                      language,
                    )}
                  </HUText>
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {item.getWordingCouponDate(offer)}
                  </HUText>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {item.status === BoxOfferInvitationStatus.PENDING ? (
          <div className="flex flex-row-reverse w-full">
            <HUButton
              onClick={handleCancel}
              type="Rounded"
              size="S"
              colorType="Secondary"
              icon={{
                iconView: <Trash2 size={16} color={theme.mainWhite} />,
              }}
            />
          </div>
        ) : null}
      </motion.div>
    </div>
  )
}
